import './dateRangePickerStyles.css'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { DateRangePicker, CustomProvider } from 'rsuite'
import ptBR from 'rsuite/locales/pt_BR'
const { afterToday } = DateRangePicker
import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material'
import { Search } from '@mui/icons-material'
import {
  CustomTableCell,
  Filter,
  GroupedFilterTags,
  TableHead,
} from '../../components'
import { getComparator } from '../../utils/graph'
import filterDrawerHelper from '../../utils/filterDrawer'
import { STATUS } from '../../utils/notificationStatus'
import useViewport from '../../hooks/useViewport'
import { useNotificationsPage } from '../../contexts/NotificationContext'
import { useTranslation } from "react-i18next"

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: 16px 0;
`

const SearchContainer = styled.div`
  display: flex;
  flex: 2;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid #d6d7db;
  border-radius: 4px;
  padding: 4px 8px;
  height: 40px;
  gap: 8px;
`

const StyledInput = styled.input`
  border: 0;
  width: 100%;
  outline: none;
`

const StickyTableCell = styled(TableCell)`
  min-width: 50px;
  left: 0;
  position: sticky;
  z-index: 2;
  background-color: #fff;
`

const StyledDateRangePicker = styled(DateRangePicker)`
  height: 20px;
`

const ContentTab = ({
  page,
  onChangePage,
  loadingData,
  rows,
  inputValue,
  handleType,
  dateRange,
  handleDateRange,
  selectedFilterOptions,
  setOpenFilterDrawer,
  onRead,
}) => {
  const { t } = useTranslation('common')
  const headState = [
    {
      id: 'employee',
      numeric: false,
      disablePadding: true,
      label: t('notifications.tab.employee'),
      align: 'left',
      sticky: true,
    },
    {
      id: 'message',
      numeric: false,
      disablePadding: false,
      label: t('notifications.tab.message'),
      align: 'left',
    },
    {
      id: 'date',
      numeric: false,
      disablePadding: false,
      label: t('notifications.tab.date'),
      align: 'left',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: t('notifications.tab.status'),
      align: 'left',
    },
    {
      id: 'severity',
      numeric: false,
      disablePadding: false,
      label: t('notifications.tab.severity'),
      align: 'left',
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: false,
      label: t('notifications.tab.actions'),
      align: 'left',
    },
  ]

  const rowsPerPage = 10

  const translateSeverities = {
    low: t('notifications.tab.low'),
    medium: t('notifications.tab.medium'),
    high: t('notifications.tab.high'),
  }

  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState(headState[3].id)
  const { width } = useViewport()
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_, newPage) => {
    onChangePage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  )

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FiltersContainer>
          <SearchContainer>
            <Search sx={{ color: '#858B92' }} />
            <StyledInput
              placeholder={t('notifications.tab.search_placeholder')}
              value={inputValue}
              onChange={handleType}
            />
          </SearchContainer>
          <div style={{ display: 'flex', gap: 8 }}>
            <CustomProvider locale={ptBR}>
              <StyledDateRangePicker
                defaultCalendarValue={dateRange}
                value={dateRange}
                onChange={value => handleDateRange({ value })}
                showOneCalendar
                shouldDisableDate={afterToday()}
                character=' - '
                placement='bottom'
                placeholder={t('notifications.tab.date_placeholder')}
                ranges={[]}
              />
            </CustomProvider>
            <Filter onClick={() => setOpenFilterDrawer(true)} />
          </div>
        </FiltersContainer>
        {selectedFilterOptionsList({
          selectedFilterOptions,
        }).length > 0 && (
          <GroupedFilterTags
            tags={selectedFilterOptionsList({
              selectedFilterOptions,
            })}
            onTagClick={() => setOpenFilterDrawer(prev => !prev)}
            maxTagsToShow={getMaxTagsToShow({ width })}
          />
        )}
      </div>
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby='notificationsTable'
        >
          <TableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            headCells={headState}
          />
          <TableBody>
            {!loadingData ? (
              <>
                {rows.length > 0 ? (
                  <>
                    {visibleRows.map((row, index) => {
                      const labelId = `enhanced-table-checkbox-${index}`

                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row.id}
                        >
                          <StickyTableCell
                            component='td'
                            id={labelId}
                            scope='row'
                            padding='none'
                            sx={{ cursor: 'pointer' }}
                            width={120}
                            onClick={() => {
                              setEmployee({
                                id: row.id,
                                name: row.employee,
                              })
                            }}
                          >
                            {row.employee}
                          </StickyTableCell>
                          <CustomTableCell data={row.message} />
                          <CustomTableCell
                            data={row.date}
                            type='date'
                            width={180}
                          />
                          <CustomTableCell
                            data={row.status}
                            type='action'
                          />
                          <CustomTableCell
                            data={translateSeverities[row.severity]}
                          />
                          <CustomTableCell
                            data={
                              <Checkbox
                                checked={row.status === STATUS.ACKNOWLEDGED}
                              />
                            }
                            width={75}
                            onClick={() =>
                              onRead({
                                id: row.id,
                                acknowledged:
                                  row.status === STATUS.ACKNOWLEDGED,
                              })
                            }
                            style={{ cursor: 'pointer' }}
                          />
                        </TableRow>
                      )
                    })}
                    {emptyRows() > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows(),
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow style={{ height: 53 * 10 }}>
                    <TableCell
                      colSpan={6}
                      align='center'
                    >
                      {t('no_data')}
                    </TableCell>
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow style={{ height: 53 * 10 }}>
                <TableCell
                  colSpan={6}
                  align='center'
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component='div'
        count={rows.length}
        showFirstButton
        showLastButton
        labelRowsPerPage={''}
        rowsPerPageOptions={[]}
        rowsPerPage={10}
        page={page}
        onPageChange={handleChangePage}
        labelDisplayedRows={({ to, count }) => {t('notifications.tab.displayed_rows', { to: to, count: count })}}
      />
    </>
  )
}

export default ContentTab
